/**
 * Centralized action helper for FK-navigation entry points.
 *
 * Three behaviors share one entry point so every drill site emits the same
 * analytics payloads and side-effects.
 *
 * - `drill-in-current`: caller pushes the row/table node onto the current
 *   tab's `navigationTree` (the side-effect is owned by the tab — this
 *   helper only fires analytics + returns a payload).
 * - `new-explorer`: opens a new Table Explorer tab on the target.
 * - `query-editor`: opens a new Query Editor tab with `SELECT *` SQL.
 */

import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { openTableExplorerTab } from '../../table-explorer/utils/openTableExplorerTab';
import { buildDrillSql, type DrillTarget } from '../utils/buildDrillSql';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { FilterGroup } from '../../core/types/workspaceTypes';

export type SecondarySource =
  | 'fk-popover-menu'
  | 'row-context-menu'
  | 'related-section-menu'
  | 'related-row-menu'
  | 'modifier-click-fk'
  | 'modifier-click-mini-grid';

export type OpenInNewExplorerArgs = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly target: DrillTarget;
  /** Additional filter (e.g. row PK or inbound-FK constraint). */
  readonly filters?: ReadonlyArray<FilterGroup>;
  /** Optional override for the title; default uses schema.table. */
  readonly title?: string;
};

export function openInNewExplorer(args: OpenInNewExplorerArgs): void {
  const { connectionId, database, target, filters, title } = args;
  // Row drills filter by PK; table drills don't add extra filters here unless caller does.
  const builtFilters: FilterGroup[] =
    target.kind === 'row'
      ? target.pk.map((p) => ({
          field: p.column,
          conditions: [{ operator: '=', value: p.value as string | number | boolean | Date | null }],
        }))
      : (filters as FilterGroup[] | undefined) ?? [];
  openTableExplorerTab({
    connectionId,
    database,
    schema: target.schema,
    tableName: target.table,
    title: title ?? `${target.schema ? `${target.schema}.` : ''}${target.table}`,
    filters: builtFilters,
  });
}

export type OpenInQueryEditorArgs = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly target: DrillTarget;
  /** Optional connection alias for the new tab's connectionInfo. */
  readonly connectionName?: string;
  readonly serverHost?: string;
};

export function openInQueryEditor(args: OpenInQueryEditorArgs): string {
  const { connectionId, database, engineId, target, connectionName, serverHost } = args;
  const query = buildDrillSql(target, engineId);
  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();
  addTab({
    title: `Query-${queryNumber}`,
    type: 'query',
    content: { query },
    connectionInfo: {
      connectionId,
      connectionName: connectionName ?? '',
      serverHost: serverHost ?? '',
      database,
    },
  });
  return query;
}
